/**
 * Pinia store holding the current operator's admin context.
 * Fetched once at admin app entry. `hasGroup(g)` returns true for
 * superusers regardless of group, and for explicit members otherwise.
 */
import { defineStore } from 'pinia';
import { fetchAdminMe } from './api';
import type { AdminGroup, AdminMe } from './types';
import { isNormalizedError } from '@/admin/shared/api/httpClient';

interface State {
    me: AdminMe | null;
    loading: boolean;
    accessDenied: boolean;
    error: string | null;
}

export const useAdminMeStore = defineStore('admin-me', {
    state: (): State => ({
        me: null,
        loading: false,
        accessDenied: false,
        error: null,
    }),
    getters: {
        email: (s): string => s.me?.email ?? '',
        displayName: (s): string => s.me?.displayName ?? '',
        isSuperuser: (s): boolean => Boolean(s.me?.isSuperuser),
        adminGroups: (s): AdminGroup[] => s.me?.adminGroups ?? [],
        // Признак «пользователь — оператор-админ» для FE-гейтов вне админ-панели
        // (роут/навигация каталога наставников). Тот же критерий, что у бэкенда
        // /admin-api/me/: суперюзер ИЛИ хотя бы одна admin.*-группа.
        isAdmin: (s): boolean =>
            !!(s.me && (s.me.isSuperuser || s.me.adminGroups.length > 0)),
        hasGroup(s) {
            return (group: AdminGroup): boolean => {
                if (!s.me) return false;
                if (s.me.isSuperuser) return true;
                return s.me.adminGroups.includes(group);
            };
        },
    },
    actions: {
        async load(): Promise<void> {
            this.loading = true;
            this.error = null;
            this.accessDenied = false;
            try {
                this.me = await fetchAdminMe();
            } catch (e) {
                if (isNormalizedError(e) && e.status === 403) {
                    this.accessDenied = true;
                    this.me = null;
                } else if (isNormalizedError(e)) {
                    this.error = e.message;
                } else {
                    this.error = 'Не удалось загрузить контекст администратора';
                }
            } finally {
                this.loading = false;
            }
        },
    },
});
